import { fieldSurface } from '../field-focus'
import { labelContent } from './labelContent'
import { useId, useState, useRef, useEffect } from 'react'

interface TextareaProps {
  label?: string
  /** Accessible name when there is no visible label. */
  ariaLabel?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  help?: string
  disabled?: boolean
  rows?: number
  maxLength?: number
  className?: string
  id?: string
}

/** Styled to match TextInput: background-input, 0.5px border, focus shifts to border-active. */
export function Textarea({
  label, ariaLabel, value, onChange, placeholder, error, help, disabled, rows = 4, maxLength, className = '', id,
}: TextareaProps) {
  const [focused, setFocused] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const generatedId = useId()
  const fieldId = id ?? generatedId

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.max(el.scrollHeight, rows * 18 + 16)}px`
  }, [value, rows])

  const { borderColor, backgroundColor } = fieldSurface({ focused, error: Boolean(error) })

  return (
    <div className={`flex flex-col ${className}`} style={{ gap: 8 }}>
      {label && (
        <label htmlFor={fieldId} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
          {labelContent(label)}
        </label>
      )}
      <textarea
        ref={ref}
        id={fieldId}
        {...(label ? {} : { 'aria-label': ariaLabel ?? placeholder })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={rows}
        className="w-full bg-transparent font-jakarta text-text-input placeholder:text-text-description outline-none resize-none"
        style={{
          backgroundColor, border: `0.5px solid ${borderColor}`, padding: '8px 12px',
          fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px', caretColor: '#FFFFFF',
          opacity: disabled ? 0.6 : 1, transition: 'background-color 0.15s ease',
        }}
      />
      <div className="flex items-start justify-between" style={{ gap: 12 }}>
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: error ? '#F05042' : '#969696' }}>
          {error ?? help ?? ''}
        </span>
        {maxLength && (
          <span className="font-mulish flex-shrink-0" style={{ fontSize: 10, lineHeight: '12px', color: '#969696' }}>
            {value.length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  )
}
